import {
  Breadcrumbs,
  Button,
  Inline,
  Notification,
  Stack,
  Switcher,
  Typography,
  useViewport,
} from '@omnipresentgroup/design-system';
import { useEmployee } from '@omnipresentgroup/domains';
import { EMPLOYEE_ID_PARAM } from '@omnipresentgroup/shared';
import type { CompensationSource } from '@omnipresentgroup/types';
import { getCountryCode } from 'countries-list';
import { useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router';

import { EmployeeSelector } from '../../components';
import {
  ADD_ADDITIONAL_PAY_PATH,
  ADDITIONAL_PAY_DETAILS_PATH,
} from '../../constants';
import {
  COMPENSATION_SOURCE_PARAM,
  CompensationSourceSelector,
  VariantPaymentsList,
} from '../../features/variant-payments';
import type { EmployeeData } from '../../features/variant-payments/types';

const PAGE_TITLE = 'Additional pay';

export const AdditionalPay = () => {
  const navigate = useNavigate();
  const [searchParameters, setSearchParameters] = useSearchParams();

  const employeeId = new URLSearchParams(searchParameters).get(
    EMPLOYEE_ID_PARAM,
  ) as string;

  const source = new URLSearchParams(searchParameters).get(
    COMPENSATION_SOURCE_PARAM,
  ) as CompensationSource;

  const { data: employee } = useEmployee({
    id: employeeId,
    enabled: Boolean(employeeId),
  });

  const employeeData = useMemo((): EmployeeData | undefined => {
    return employee
      ? {
          id: employee.id,
          currency: employee.employmentDetails.annualBaseSalary.currency,
          countryCode: (getCountryCode(employee.country.name) || '') as string,
        }
      : undefined;
  }, [employee]);

  const PageActions = () => {
    return (
      <Button
        onClick={() =>
          navigate(
            `${ADD_ADDITIONAL_PAY_PATH}${
              employeeId ? `?${EMPLOYEE_ID_PARAM}=${employeeId}` : ''
            }`,
          )
        }
      >
        Add
      </Button>
    );
  };

  const PageHeader = () => {
    return (
      <Inline justify="space-between" minH={40} align="center">
        <Typography as="h2" hideParagraphSpacing size="24" weight="medium">
          {PAGE_TITLE}
        </Typography>
        <PageActions />
      </Inline>
    );
  };

  const Filters = () => {
    const { isPhone, isTablet } = useViewport();

    const isStacked = useMemo(() => isPhone || isTablet, [isPhone, isTablet]);

    return (
      <Switcher isStacked={isStacked}>
        <Inline maxW={isStacked ? undefined : 400} w="stretch" stretch={1}>
          <EmployeeSelector label="Employee" />
        </Inline>
        <Inline maxW={isStacked ? undefined : 250} w="stretch" stretch={1}>
          <CompensationSourceSelector label="Category" />
        </Inline>
        <Inline align="end" justify="end">
          <Button
            variant="tertiary"
            icon="FilterOff"
            onClick={() => {
              setSearchParameters((parameters) => {
                parameters.delete(EMPLOYEE_ID_PARAM);
                parameters.delete(COMPENSATION_SOURCE_PARAM);
                return parameters;
              });
            }}
          >
            Clear
          </Button>
        </Inline>
      </Switcher>
    );
  };

  return (
    <>
      <Stack gap="16">
        <Breadcrumbs
          crumbs={[
            { id: 'home', title: 'Home', onClick: () => navigate('/') },
            {
              id: 'additional-pay',
              title: PAGE_TITLE,
              active: true,
            },
          ]}
        />
        <PageHeader />
      </Stack>
      <Notification
        id="cut-off-date-notice"
        type="banner"
        intent="info"
        title="Important: When requesting additional payments, you can verify the employee's cutoff date and when these changes will be effective from once you click on the page to fill in the details of the payment."
      />
      <VariantPaymentsList
        employeeData={employeeData}
        source={source}
        columnVisibility={{
          employeeName: true,
          source: true,
          type: true,
          recurrence: true,
          amount: true,
          effectiveDate: true,
          endDate: true,
          state: true,
        }}
        onRowClick={(row) => {
          navigate(ADDITIONAL_PAY_DETAILS_PATH(row.id));
        }}
        filterSection={<Filters />}
      />
    </>
  );
};
